import React, { useState, useEffect, useRef } from 'react';
import GraphCanvas from './GraphCanvas';
import { subscribeToGraphUpdates, fetchNodeLogs } from '../utils/graphApiService';
import './GraphView.css';

/**
 * GraphView Component
 * Main page component for LangGraph-style visualization
 */
function GraphView({ sessionId, userId, onBack, onHideChat, isChatHidden }) {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [activeEdgeId, setActiveEdgeId] = useState(null);
  const [activeNodeId, setActiveNodeId] = useState(null); // Currently processing node
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [nodeLogs, setNodeLogs] = useState([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [messageHistory, setMessageHistory] = useState([]);
  const [activeLogTab, setActiveLogTab] = useState('all'); // 'all', 'node_processing', 'edge_activation'
  const subscriptionRef = useRef(null);
  const activeNodeTimeoutRef = useRef(null); // For delayed clearing of active node
  
  // Load initial graph data
  useEffect(() => {
    loadGraphData();
    
    // Subscribe to real-time updates
    if (sessionId) {
      const cleanup = subscribeToGraphUpdates(sessionId, handleGraphUpdate);
      subscriptionRef.current = cleanup;
    }
    
    return () => {
      // Cleanup subscription
      if (subscriptionRef.current) {
        subscriptionRef.current();
        subscriptionRef.current = null;
      }
      // Cleanup timeout
      if (activeNodeTimeoutRef.current) {
        clearTimeout(activeNodeTimeoutRef.current);
        activeNodeTimeoutRef.current = null;
      }
    };
  }, [sessionId, userId]);
  
  const loadGraphData = () => {
    setIsLoading(true);
    setError(null);
    
    // Use frontend default graph configuration
    initializeDefaultGraph();
    
    setIsLoading(false);
  };
  
  // Helper function to generate edge ID (same format as backend)
  const getEdgeId = (from, to) => `e_${from}_${to}`;
  
  const initializeDefaultGraph = () => {
    // Default graph structure
    const defaultNodes = [
      { id: 'user', label: 'User', type: 'user', status: 'idle',x: 50, y: 100 },
      { id: 'user-agent', label: 'User Agent', type: 'user-agent', status: 'idle',x: 50, y: 260 },
      { id: 'flight-mcp', label: 'Flight MCP', type: 'flight-mcp', status: 'idle' ,x: 360, y: 300},
      { id: 'hotel-mcp', label: 'Hotel MCP', type: 'hotel-mcp', status: 'idle' ,x: 360, y: 420},
      { id: 'merchant-agent', label: 'Merchant Agent', type: 'merchant-agent', status: 'idle' ,x: 360, y: 550},
      { id: 'acquirer', label: 'Acquirer', type: 'acquirer', status: 'idle' ,x: 360, y: 670},
      { id: 'payment-processor', label: 'Mastercard', type: 'payment-processor', status: 'idle' ,x: 50, y: 780},
      { id: 'issuer', label: 'Issuer', type: 'issuer', status: 'idle' ,x: 50, y: 900},
     
    ];
    
    const defaultEdges = [
      { id: getEdgeId('user', 'payment-processor'), from: 'user', to: 'payment-processor', label: '', messageCount: 0 },
      { id: getEdgeId('user', 'user-agent'), from: 'user', to: 'user-agent', label: '', messageCount: 0 },
      { id: getEdgeId('user-agent', 'flight-mcp'), from: 'user-agent', to: 'flight-mcp', label: '', messageCount: 0 },
      { id: getEdgeId('user-agent', 'hotel-mcp'), from: 'user-agent', to: 'hotel-mcp', label: '', messageCount: 0 },
      { id: getEdgeId('user-agent', 'merchant-agent'), from: 'user-agent', to: 'merchant-agent', label: '', messageCount: 0 },
      { id: getEdgeId('user-agent', 'issuer'), from: 'user-agent', to: 'issuer', label: '', messageCount: 0 },
      
      // { id: getEdgeId('merchant-agent', 'payment-processor'), from: 'merchant-agent', to: 'payment-processor', label: '', messageCount: 0 }, AP2 have this
      { id: getEdgeId('user-agent', 'payment-processor'), from: 'user-agent', to: 'payment-processor', label: '', messageCount: 0 },
      { id: getEdgeId('payment-processor', 'issuer'), from: 'payment-processor', to: 'issuer', label: '', messageCount: 0 },
      { id: getEdgeId('issuer', 'payment-processor'), from: 'issuer', to: 'payment-processor', label: '', messageCount: 0 },
      { id: getEdgeId('merchant-agent', 'acquirer'), from: 'merchant-agent', to: 'acquirer', label: '', messageCount: 0 },
      { id: getEdgeId('acquirer', 'payment-processor'), from: 'acquirer', to: 'payment-processor', label: '', messageCount: 0 },
      { id: getEdgeId('payment-processor', 'acquirer'), from: 'payment-processor', to: 'acquirer', label: '', messageCount: 0 },
      { id: getEdgeId('issuer', 'user-agent'), from: 'issuer', to: 'user-agent', label: '', messageCount: 0 },
      
      { id: getEdgeId('payment-processor', 'user-agent'), from: 'payment-processor', to: 'user-agent', label: '', messageCount: 0 }
    ];
    
    setNodes(defaultNodes);
    setEdges(defaultEdges);
  };
  
  const handleGraphUpdate = (update) => {
    // console.log('Graph update received:', update);
    
    if (update.type === 'node_processing') {
      // Node processing: start, processing, or end
      const nodeId = update.nodeId;
      const status = update.status || 'processing'; // 'start', 'processing', 'end'
      
      // Clear previous active edge (if any)
      setActiveEdgeId(null);
      
      // Clear any existing timeout for active node
      if (activeNodeTimeoutRef.current) {
        clearTimeout(activeNodeTimeoutRef.current);
        activeNodeTimeoutRef.current = null;
      }
      
      // Set this node as active
      setActiveNodeId(nodeId);
      
      // If node is completed (status='end'), clear active state after 2 seconds
      if (status === 'end') {
        activeNodeTimeoutRef.current = setTimeout(() => {
          setActiveNodeId(null);
          activeNodeTimeoutRef.current = null;
        }, 3000); // 2 seconds delay
      }
      
      // Update node status based on processing status
      setNodes(prev => prev.map(node => 
        node.id === nodeId 
          ? { 
              ...node, 
              status: status === 'end' ? 'completed' : 'active',
              metadata: {
                ...node.metadata,
                lastProcessingMessage: update.message || update.content,
                processingCount: (node.metadata?.processingCount || 0) + 1,
                // Store request data (when status='start')
                request: status === 'start' ? update.request : node.metadata?.request,
                // Store result data (when status='end')
                result: status === 'end' ? update.result : node.metadata?.result
              }
            }
          : node
      ));
      
      // Add to message history (with deduplication)
      setMessageHistory(prev => {
        // Normalize timestamp to number (milliseconds)
        const updateTimestamp = update.timestamp 
          ? (typeof update.timestamp === 'number' ? update.timestamp : (typeof update.timestamp === 'string' ? new Date(update.timestamp).getTime() : update.timestamp))
          : Date.now();
        const messageContent = update.message || update.content || 'Processing...';
        
        // Check if this message already exists (same node, same status, same message, within 5 seconds)
        const exists = prev.some(msg => {
          if (msg.type !== 'node_processing' || msg.nodeId !== nodeId || msg.status !== status || msg.message !== messageContent) {
            return false;
          }
          // msg.timestamp is now always a number
          const msgTimestamp = typeof msg.timestamp === 'number' ? msg.timestamp : (msg.timestamp instanceof Date ? msg.timestamp.getTime() : new Date(msg.timestamp).getTime());
          // Allow 5 second window for duplicate detection
          const timeDiff = Math.abs(updateTimestamp - msgTimestamp);
          return timeDiff < 5000; // 5 seconds
        });
        
        if (exists) {
          console.log('Duplicate node_processing message detected, skipping:', {
            nodeId: nodeId,
            status: status,
            message: messageContent,
            timestamp: updateTimestamp
          });
          return prev;
        }
        
        // Store timestamp as number (milliseconds)
        const messageTimestamp = updateTimestamp;
        const newMessage = {
          id: messageTimestamp,
          timestamp: messageTimestamp,
          nodeId: nodeId,
          type: 'node_processing',
          status: status,
          message: messageContent,
          request: update.request,
          result: update.result
        };
        // Insert and sort by timestamp to maintain sorted order
        return [...prev, newMessage].sort((a, b) => {
          const timeA = typeof a.timestamp === 'number' ? a.timestamp : new Date(a.timestamp).getTime();
          const timeB = typeof b.timestamp === 'number' ? b.timestamp : new Date(b.timestamp).getTime();
          return timeA - timeB;
        });
      });
      
    } else if (update.type === 'edge_activation') {
      // Message flow between nodes - highlight edge and keep it active
      const edgeId = update.edgeId;
      const fromNode = update.from;
      const toNode = update.to;
      
      // Clear previous active node (if any)
      setActiveNodeId(null);
      
      // Check for duplicates and update both messageHistory and edges atomically
      if (update.message) {
        // Normalize timestamp to number (milliseconds)
        const updateTimestamp = update.timestamp 
          ? (typeof update.timestamp === 'number' ? update.timestamp : (typeof update.timestamp === 'string' ? new Date(update.timestamp).getTime() : update.timestamp))
          : Date.now();
        
        // Use functional updates to check duplicates and update both states atomically
        setMessageHistory(prevHistory => {
          // Check if this message already exists (same edge, same message, within 5 seconds)
          const isDuplicate = prevHistory.some(msg => {
            if (msg.type !== 'edge_activation' || msg.edgeId !== edgeId || msg.message !== update.message) {
              return false;
            }
            // msg.timestamp is now always a number
            const msgTimestamp = typeof msg.timestamp === 'number' ? msg.timestamp : (msg.timestamp instanceof Date ? msg.timestamp.getTime() : new Date(msg.timestamp).getTime());
            // Allow 5 second window for duplicate detection
            const timeDiff = Math.abs(updateTimestamp - msgTimestamp);
            return timeDiff < 5000; // 5 seconds
          });
          
          if (isDuplicate) {
            console.log('Duplicate edge_activation message detected, skipping:', {
              edgeId: edgeId,
              message: update.message,
              timestamp: updateTimestamp
            });
            return prevHistory; // Don't add to history
          }
          
          // Not a duplicate - update edges and nodes in the same batch
          setEdges(prevEdges => {
            let targetEdge = prevEdges.find(e => e.id === edgeId);
        
        // If edgeId doesn't match, try to find by from/to
        if (!targetEdge && fromNode && toNode) {
              targetEdge = prevEdges.find(e => e.from === fromNode && e.to === toNode);
        }
        
        if (!targetEdge) {
          console.warn(`Edge not found: edgeId=${edgeId}, from=${fromNode}, to=${toNode}`);
              return prevEdges;
        }
        
        // Set this edge as active (will stay active until next update)
        setActiveEdgeId(targetEdge.id);
        
            // Update edge message count (only if not duplicate)
            const updatedEdges = prevEdges.map(edge => 
          edge.id === targetEdge.id
            ? { ...edge, messageCount: (edge.messageCount || 0) + 1 }
            : edge
        );
        
        // Update node statuses
        setNodes(prevNodes => prevNodes.map(node => {
          if (node.id === targetEdge.from) {
            return { ...node, status: 'completed' };
          } else if (node.id === targetEdge.to) {
            return { ...node, status: 'active' };
          }
          return node;
        }));
            
            return updatedEdges;
          });
        
        // Add to message history - store timestamp as number (milliseconds)
          const newMessage = {
            id: updateTimestamp,
            timestamp: updateTimestamp,
            edgeId: edgeId,
            from: fromNode,
            to: toNode,
            type: 'edge_activation',
            message: update.message,
            data: update.data
          };
          // Insert and sort by timestamp to maintain sorted order
          return [...prevHistory, newMessage].sort((a, b) => {
            const timeA = typeof a.timestamp === 'number' ? a.timestamp : new Date(a.timestamp).getTime();
            const timeB = typeof b.timestamp === 'number' ? b.timestamp : new Date(b.timestamp).getTime();
            return timeA - timeB;
          });
        });
      } else {
        // No message, just update edge status without counting
        setEdges(prevEdges => {
          let targetEdge = prevEdges.find(e => e.id === edgeId);
          
          if (!targetEdge && fromNode && toNode) {
            targetEdge = prevEdges.find(e => e.from === fromNode && e.to === toNode);
          }
          
          if (!targetEdge) {
            return prevEdges;
          }
          
          setActiveEdgeId(targetEdge.id);
          
          setNodes(prevNodes => prevNodes.map(node => {
            if (node.id === targetEdge.from) {
              return { ...node, status: 'completed' };
            } else if (node.id === targetEdge.to) {
              return { ...node, status: 'active' };
            }
            return node;
          }));
          
          return prevEdges;
      });
      }
    }
  };
  
  const handleNodeClick = async (node) => {
    setSelectedNode(node);
    // Load detailed logs for this node
    if (node && node.id) {
      setIsLoadingLogs(true);
      try {
        const logsData = await fetchNodeLogs(node.id, sessionId);
        setNodeLogs(logsData.logs || []);
      } catch (error) {
        console.error('Failed to load node logs:', error);
        setNodeLogs([]);
      } finally {
        setIsLoadingLogs(false);
      }
    } else {
      setNodeLogs([]);
    }
  };
  
  const handleEdgeClick = (edgeId) => {
    setActiveEdgeId(edgeId);
  };
  
  if (isLoading) {
    return (
      <div className="graph-view">
        <div className="graph-loading">
          <div className="loading-spinner" />
          <p>Loading graph...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="graph-view">
        <div className="graph-error">
          <p>Error: {error}</p>
          <button onClick={loadGraphData}>Retry</button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="graph-view">
      <div className="graph-header">
        <div className="graph-header-left">
          <button className="back-button" onClick={isChatHidden ? onBack : (onHideChat || onBack)}>
            {isChatHidden ? '← Show Chat' : (onHideChat ? 'Hide Chat' : '← Back to Chat')}
          </button>
          <h1>Agent Flow Graph</h1>
        </div>
        <div className="graph-header-right">
          <button className="refresh-button" onClick={loadGraphData}>
            🔄 Refresh
          </button>
          <div className="session-info">
            Session: {sessionId ? sessionId : 'N/A'}
          </div>
        </div>
      </div>
      
      <div className="graph-content">
        <div className="graph-main">
          <GraphCanvas
            nodes={nodes}
            edges={edges}
            activeEdgeId={activeEdgeId}
            activeNodeId={activeNodeId}
            onNodeClick={handleNodeClick}
            onEdgeClick={handleEdgeClick}
          />
        </div>
        
        <div className="graph-sidebar">
          <div className="sidebar-section">
            <h3>Node Details</h3>
            {selectedNode ? (
              <div className="node-details">
                <div className="detail-item">
                  <strong>ID:</strong> {selectedNode.id}
                </div>
                <div className="detail-item">
                  <strong>Type:</strong> {selectedNode.type}
                </div>
                <div className="detail-item">
                  <strong>Name:</strong> {selectedNode.label}
                </div>
                
              </div>
            ) : (
              <p className="no-selection">Click a node to view details</p>
            )}
          </div>
          
          {selectedNode && (
            <div className="sidebar-section">
              <h3>Node Logs ({nodeLogs.length})</h3>
              {isLoadingLogs ? (
                <p className="loading-logs">Loading logs...</p>
              ) : nodeLogs.length === 0 ? (
                <p className="no-logs">No logs available for this node</p>
              ) : (
                <>
                  <div className="log-tabs">
                    <button
                      className={`log-tab ${activeLogTab === 'all' ? 'active' : ''}`}
                      onClick={() => setActiveLogTab('all')}
                    >
                      All ({nodeLogs.length})
                    </button>
                    <button
                      className={`log-tab ${activeLogTab === 'node_processing' ? 'active' : ''}`}
                      onClick={() => setActiveLogTab('node_processing')}
                    >
                      Processing ({nodeLogs.filter(log => log.type === 'node_processing').length})
                    </button>
                    <button
                      className={`log-tab ${activeLogTab === 'edge_activation' ? 'active' : ''}`}
                      onClick={() => setActiveLogTab('edge_activation')}
                    >
                      Activation ({nodeLogs.filter(log => log.type === 'edge_activation').length})
                    </button>
                  </div>
                  <div className="node-logs">
                    {(() => {
                      const filteredLogs = activeLogTab === 'all'
                        ? nodeLogs
                        : nodeLogs.filter(log => log.type === activeLogTab);
                      
                      if (filteredLogs.length === 0) {
                        return <p className="no-logs">No {activeLogTab === 'all' ? '' : activeLogTab.replace('_', ' ')} logs available</p>;
                      }
                      
                      return filteredLogs.map((log, index) => (
                        <div key={index} className={`log-entry ${log.type === 'node_processing' ? 'log-processing' : ''}`}>
                          <div className={`log-header ${log.to}`}>
                            <span className="log-type">{log.type || 'unknown'}</span>
                            <span className="log-time">
                              {log.timestamp ? (() => {
                                // Ensure timestamp is a number (UTC milliseconds)
                                const timestamp = typeof log.timestamp === 'number' ? log.timestamp : parseInt(log.timestamp);
                                // new Date(timestamp) automatically converts UTC timestamp to local timezone
                                const date = new Date(timestamp);
                                // Use getHours() which returns local time (already converted from UTC)
                                const hours = date.getHours().toString().padStart(2, '0');
                                const minutes = date.getMinutes().toString().padStart(2, '0');
                                const seconds = date.getSeconds().toString().padStart(2, '0');
                                return `${hours}:${minutes}:${seconds}`;
                              })() : ''}
                            </span>
                          </div>
                          <div className={`log-content ${log.to}`}>
                            {log.message && (
                              <div className="log-message">{log.message}</div>
                            )}
                            {(log.from && log.to && log.status) ? (
                              <div className={`log-status ${log.to}`}>
                                {log.from} → {log.to} : <strong>{log.status}</strong>
                              </div>
                            ) : log.status ? (
                              <div className="log-status">
                                Status: <strong>{log.status}</strong>
                              </div>
                            ) : null}
                            {log.request && (
                              <details className="log-data-details">
                                <summary>📥 Request Data</summary>
                                <pre className="log-json">
                                  {JSON.stringify(log.request, null, 2)}
                                </pre>
                              </details>
                            )}
                            {log.result && (
                              <details className="log-data-details">
                                <summary>📤 Result Data</summary>
                                <pre className="log-json">
                                  {JSON.stringify(log.result, null, 2)}
                                </pre>
                              </details>
                            )}
                            {log.data && (
                              <details className="log-data-details">
                                <summary>📦 Message Data</summary>
                                <pre className="log-json">
                                  {JSON.stringify(log.data, null, 2)}
                                </pre>
                              </details>
                            )}
                            <details className="log-details">
                              <summary>View Full Details</summary>
                              <pre className="log-json">
                                {JSON.stringify(log, null, 2)}
                              </pre>
                            </details>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </>
              )}
            </div>
          )}
          
          <div className="sidebar-section">
            <h3>Message History</h3>
            <div className="message-history">
              {(() => {
                const edgeMessages = messageHistory.filter(msg => msg.type === 'edge_activation');
                if (edgeMessages.length === 0) {
                  return <p className="no-messages">No messages yet</p>;
                }
                
                // Sort by timestamp (ascending) and take last 30, then reverse for display (newest first)
                // timestamp is now always a number (milliseconds)
                const sortedMessages = [...edgeMessages]
                  .sort((a, b) => {
                    const timeA = typeof a.timestamp === 'number' ? a.timestamp : (a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime());
                    const timeB = typeof b.timestamp === 'number' ? b.timestamp : (b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime());
                    return timeA - timeB;
                  })
                  .slice(-30)
                  .reverse();
                
                return sortedMessages.map(msg => {
                  // Convert timestamp (number in milliseconds) to Date for display
                  const timestamp = typeof msg.timestamp === 'number' 
                    ? msg.timestamp 
                    : (msg.timestamp instanceof Date ? msg.timestamp.getTime() : new Date(msg.timestamp).getTime());
                  
                  // Format timestamp to local time with date and time
                  // Backend sends UTC timestamp in milliseconds
                  // IMPORTANT: Ensure timestamp is treated as UTC, not local time
                  const formatLocalTime = (ts) => {
                    if (!ts) return '';
                    
                    // Ensure ts is a number (milliseconds since epoch in UTC)
                    const timestamp = typeof ts === 'number' ? ts : parseInt(ts);
                    
                    // Create Date object from UTC timestamp
                    // new Date(timestamp) interprets the number as milliseconds since Unix epoch (UTC)
                    // and automatically converts to browser's local timezone
                    const date = new Date(timestamp);
                    
                    // Debug log (can be removed in production)
                    if (process.env.NODE_ENV === 'development') {
                      /*console.log('Time format debug:', {
                        originalTimestamp: ts,
                        parsedTimestamp: timestamp,
                        utcISO: date.toISOString(),
                        localString: date.toString(),
                        localHours: date.getHours(),
                        utcHours: date.getUTCHours(),
                        offset: date.getTimezoneOffset() / -60
                      });*/
                    }
                    
                    const now = new Date();
                    const isToday = date.toDateString() === now.toDateString();
                    
                    if (isToday) {
                      // Today: show time only
                      // Use getHours() which returns local time (already converted from UTC)
                      const hours = date.getHours().toString().padStart(2, '0');
                      const minutes = date.getMinutes().toString().padStart(2, '0');
                      const seconds = date.getSeconds().toString().padStart(2, '0');
                      return `${hours}:${minutes}:${seconds}`;
                    } else {
                      // Other days: show date and time
                      const month = date.toLocaleString('en-US', { month: 'short' });
                      const day = date.getDate();
                      const hours = date.getHours().toString().padStart(2, '0');
                      const minutes = date.getMinutes().toString().padStart(2, '0');
                      return `${month} ${day}, ${hours}:${minutes}`;
                    }
                  };
                  
                  return (
                    <div key={msg.id} className="message-item">
                      <div className="message-header">
                        <span className={`message-route`}>
                          <div className={`${msg.from}`}>{msg.from}</div>→
                          <div className={`${msg.to}`}>{msg.to}</div>
                        </span>
                        
                      </div>
                      <div className="message-content">
                        {msg.message}
                      </div>
                      <div className="message-time">
                        {formatLocalTime(timestamp)}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GraphView;

